import { useQuery } from "@tanstack/react-query";
import chunk from "lodash/chunk";

import { TPoolMap } from "../../types/Pool.types";
import { getAssetId, transformAssetIdToGqlAssetId } from "../../utils/assets.utils";
import { getPoolPairId } from "../../utils/pool.utils";
import { getStats2Sdk } from "../client/statsSdk";
import { PoolFragmentFragment, VolumeInterval } from "../generated/stats2.sdk";

export const usePopularPools = (pageSize?: number, staleTime = 30) => {
  const queryInfo = useQuery(
    ["popularPools", pageSize],
    async () => {
      const { getPopularPools } = await getStats2Sdk();
      return getPopularPools({ pageSize });
    },
    {
      staleTime: 1000 * staleTime,
    }
  );
  return queryInfo;
};

export const usePoolsBySearch = (searchQuery?: string) => {
  const queryInfo = useQuery(
    ["searchPools", searchQuery],
    async () => {
      const { searchPools } = await getStats2Sdk();
      const query = await searchPools({ query: searchQuery! });
      return { ...query, pools: query.pools.filter(Boolean) as PoolFragmentFragment[] };
    },
    { enabled: Boolean(searchQuery), suspense: false }
  );
  return queryInfo;
};

export const usePoolsBySearchWithStats = (searchQuery?: string) => {
  const queryInfo = useQuery(
    ["searchPoolsWithStats", searchQuery],
    async () => {
      const { searchPoolsWithStats } = await getStats2Sdk();
      return searchPoolsWithStats({
        query: searchQuery!,
        currency: "ADA",
        dateFrom: new Date(Date.now() - 86400000).toISOString(),
        dateTo: new Date().toISOString(),
        interval: VolumeInterval.Daily,
      });
    },
    { enabled: Boolean(searchQuery), suspense: false }
  );
  return queryInfo;
};

export const usePoolsByPair = ({ coinA, coinB }: { coinA?: string; coinB?: string }) => {
  const result = useQuery(
    ["poolsByPair", coinA, coinB],
    async () => {
      const { getPoolsByPair } = await getStats2Sdk();
      if ([coinA, coinB].includes(undefined)) {
        return [];
      }

      const pairs = await getPoolsByPair({ coinA: coinA as string, coinB: coinB as string });

      /**
       * @TODO
       *
       * Currently, the v1 API returns all pools if a pair is not found. So we
       * filter out any non-matching pools below to keep results accurate.
       *
       * Once v2 API is shipped, we can remove this .filter() block and just use
       * the results from the API directly.
       */
      const filteredPool = (pairs?.poolsByPair.filter(Boolean) ?? []) as PoolFragmentFragment[];
      const matchingPools = filteredPool.filter(
        ({ assetA, assetB }) =>
          (assetA.assetId === coinA && assetB.assetId === coinB) ||
          (assetA.assetId === coinB && assetB.assetId === coinA)
      );

      return matchingPools;
    },
    { retry: 2, staleTime: 1000 * 60 * 3 /* 3 min */ }
  );

  return {
    ...result,
    data: result.data ?? [],
  };
};

export const useRawPoolsByAssetIds = ({
  assetIds,
  pageSize,
  page,
}: {
  assetIds: string[];
  pageSize?: number;
  page?: number;
}) => {
  return useQuery(
    ["getPoolsByAssetIds", assetIds, pageSize, page],
    async () => {
      const { getPoolsByAssetIds } = await getStats2Sdk();

      const results = await getPoolsByAssetIds({
        assetIds: assetIds.map((id) => transformAssetIdToGqlAssetId(id)),
        pageSize,
        page,
      });

      return results.pools.filter(Boolean) as PoolFragmentFragment[];
    },
    {
      refetchOnWindowFocus: false,
    }
  );
};

export const usePoolsByAssetIds = ({
  assetIds,
  pageSize,
  page,
}: {
  assetIds: string[];
  pageSize?: number;
  page?: number;
}) => {
  return useQuery(
    ["getPoolsByAssetIds", assetIds, pageSize, page],
    async () => {
      const { getPoolsByAssetIds } = await getStats2Sdk();

      /**
       * This result will be capped to the first 50 entries - which is fine for cases like the asset select flow as we will paginate the results later.
       * However, if we want to get all matching pools for a combination of assets, we need to fetch the results for _both_ assets separately and merge them afterwards.
       */
      const resultsForInput = await getPoolsByAssetIds({
        assetIds: [assetIds[0]].map((id) => transformAssetIdToGqlAssetId(id)),
        pageSize,
        page,
      });

      // If we query for two assetIds, we need to merge the results from the first query with the results from the second query.
      const resultsForOutput = assetIds[1]
        ? await getPoolsByAssetIds({
            assetIds: [assetIds[1]].map((id) => transformAssetIdToGqlAssetId(id)),
            pageSize,
            page,
          })
        : { pools: [] };

      const data = [...resultsForInput.pools, ...resultsForOutput.pools];
      const formattedData: Record<string, PoolFragmentFragment[]> = data?.reduce((acc, pool) => {
        if (!pool) return acc;

        const pairId = getPoolPairId([getAssetId(pool.assetA), getAssetId(pool.assetB)]);
        return { ...acc, [pairId]: acc[pairId] ? [...acc[pairId], pool] : [pool] };
      }, {} as TPoolMap);

      return formattedData;
    },
    {
      enabled: assetIds.length > 0,
      staleTime: 1000 * 60,
      cacheTime: 1000 * 60,
      refetchOnWindowFocus: false,
    }
  );
};

export const usePoolByIdent = (ident?: string) => {
  const queryInfo = useQuery(
    ["poolByIdent", ident],
    async () => {
      if (!ident) return;
      const { getPoolByIdent } = await getStats2Sdk();
      return getPoolByIdent({ ident });
    },
    { enabled: Boolean(ident), suspense: false }
  );

  const data = queryInfo?.data?.poolByIdent as PoolFragmentFragment;

  return { ...queryInfo, data };
};

// getPoolsByIdent is limited to 20 idents per request, so we need to batch them
export const usePoolsByIdent = (idents?: string[]) => {
  const batchedIdents = chunk(idents ?? [], 20);
  const queryInfo = useQuery(
    ["poolsByIdent", idents],
    async () => {
      if (!idents) return;
      const { getPoolsByIdent } = await getStats2Sdk();
      return Promise.all(batchedIdents.map((idents) => getPoolsByIdent({ idents })));
    },
    { enabled: Boolean(idents), suspense: false }
  );

  const data = queryInfo?.data?.reduce((arr, item) => {
    if (!item) return arr;
    return [...arr, ...item.poolsByIdent];
  }, []) as PoolFragmentFragment[];

  return { ...queryInfo, data };
};
